import {
    ShieldCheck,
    Cpu,
    Video,
    Layers,
    DoorOpen,
    Sparkles,
} from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

/**
 * AusstattungKomfort – editorial "what's included in every apartment"
 * section. Communicates that quality, comfort, security, smart-home
 * readiness and resource-friendly long-term living are baked into every
 * unit by design. Position: between ApartmentsCompare and ConstructionTracker.
 */
export const AusstattungKomfort = () => {
    const t = useT();

    const ITEMS = [
        {
            icon: ShieldCheck,
            title: t({ de: "Einbruchsicherheit", en: "Burglary protection" }),
            text: t({
                de: "Sicherheit, die mit der Architektur entsteht: einbruchhemmende Haus- und Wohnungstüren, RC-Standard-Beschläge, Pilzkopf-Verriegelung sowie absenkbare Rollläden an allen erdnahen Fenstern.",
                en: "Security baked into the architecture: burglary-resistant front doors, RC-standard fittings, mushroom-head locking and lowerable shutters at all ground-floor-level windows.",
            }),
            tags: [
                t({ de: "RC-Beschläge", en: "RC fittings" }),
                t({ de: "Pilzkopf-Verriegelung", en: "Mushroom-head lock" }),
                t({ de: "Rollläden", en: "Shutters" }),
            ],
        },
        {
            icon: Cpu,
            title: t({ de: "Smart-Home vorbereitet", en: "Smart-home ready" }),
            text: t({
                de: "Alle Wohnungen sind ab Werk smart-home-vorbereitet (Bus-System, Leerrohre, zentrale Verteilung). Auf Wunsch erfolgt die voll integrierte Smart-Home-Installation – Heizung, Beschattung, Beleuchtung und Sicherheit über App und Sprache.",
                en: "Every apartment is smart-home-ready by default (bus system, conduits, central distribution). Optional: full smart-home installation – heating, shading, lighting and security via app and voice.",
            }),
            tags: [
                t({ de: "Bus-System", en: "Bus system" }),
                t({ de: "Vollinstallation optional", en: "Full install optional" }),
                t({ de: "App & Sprache", en: "App & voice" }),
            ],
        },
        {
            icon: Video,
            title: t({ de: "Video-Gegensprechanlage", en: "Video intercom" }),
            text: t({
                de: "Hochwertige Video-Gegensprechanlage in jeder Wohnung – sehen, wer klingelt, Tür öffnen per Knopfdruck oder mobil über die App. Komfort und Sicherheit gleichzeitig.",
                en: "Premium video intercom in every apartment – see who is ringing, open the door with one tap or remotely via the app. Comfort and security combined.",
            }),
            tags: [
                t({ de: "HD-Bild", en: "HD vision" }),
                t({ de: "App-Anbindung", en: "App-ready" }),
            ],
        },
        {
            icon: Layers,
            title: t({ de: "Dreifach-Verglasung + Rollläden", en: "Triple glazing + shutters" }),
            text: t({
                de: "Dreifach verglaste Fenster mit hochwertigen Rahmen reduzieren Wärmeverluste, dämmen Geräusche und sorgen mit elektrischen Rollläden für Sonnen- und Sichtschutz – ein Plus an Wohnqualität in jeder Jahreszeit.",
                en: "Triple-glazed windows with premium frames reduce heat loss, dampen noise and – combined with electric shutters – provide sun and privacy protection in every season.",
            }),
            tags: [
                t({ de: "Schalldämmung", en: "Acoustic insulation" }),
                t({ de: "Sonnenschutz", en: "Sun protection" }),
                t({ de: "Elektrische Rollläden", en: "Electric shutters" }),
            ],
        },
        {
            icon: DoorOpen,
            title: t({ de: "Separates Gäste-WC", en: "Separate guest WC" }),
            text: t({
                de: "Jede Wohnung verfügt über ein separates Gäste-WC – ein Komfort-Detail, das den Alltag spürbar entlastet, gerade bei Besuch oder Familien mit Kindern.",
                en: "Every apartment has a separate guest WC – a comfort detail that visibly upgrades daily life, especially when hosting guests or with kids around.",
            }),
            tags: [
                t({ de: "In allen 4 Wohnungen", en: "All 4 apartments" }),
            ],
        },
        {
            icon: Sparkles,
            title: t({ de: "Komfort, der bleibt", en: "Comfort that lasts" }),
            text: t({
                de: "Hochwertige Innentüren, Markenarmaturen, durchdachte Steckdosen-Konzepte, vorbereitete Anschlüsse für E-Mobilität und Photovoltaik – Details, die den Wohnwert über Jahrzehnte erhalten.",
                en: "Premium interior doors, brand-name fittings, well-thought-out outlet planning, prepared connections for e-mobility and PV – details that preserve living value for decades.",
            }),
            tags: [
                t({ de: "Markenarmaturen", en: "Brand-name fittings" }),
                t({ de: "Wallbox optional", en: "Wallbox optional" }),
                t({ de: "PV 2 kW + Speicher inkl.", en: "2 kW PV + storage incl." }),
            ],
        },
    ];

    return (
        <section
            id="ausstattung"
            className="bg-white text-ink py-24 sm:py-32 border-t border-stone-200"
            data-testid="ausstattung-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-12 mb-14">
                    <div className="lg:col-span-5">
                        <span className="overline" data-testid="ausstattung-eyebrow">
                            {t({ de: "Ausstattung & Komfort", en: "Equipment & comfort" })}
                        </span>
                        <h2
                            className="h-section mt-4 text-balance"
                            data-testid="ausstattung-title"
                        >
                            {t({ de: "Mehr als ein", en: "More than just a" })}
                            <span className="italic text-brand-500">
                                {t({ de: " Standard-Neubau.", en: " standard new build." })}
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="body-md">
                            {t({
                                de: (
                                    <>
                                        Architektur, Planung und Ausstattung sind so gedacht, dass Sie <strong>jetzt und in Jahrzehnten</strong>{" "}
                                        angenehm, nachhaltig und mit geringem Aufwand wohnen –
                                        sicher, energieeffizient und ressourcenschonend. Komfort ist
                                        bei G21 kein Aufpreis, sondern Standard.
                                    </>
                                ),
                                en: (
                                    <>
                                        Architecture, planning and equipment are designed so you can live{" "}
                                        <strong>now and decades from now</strong> comfortably,
                                        sustainably and with minimal effort – safe, energy-efficient
                                        and resource-friendly. At G21 comfort is not a surcharge but
                                        the standard.
                                    </>
                                ),
                            })}
                        </p>
                    </div>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden"
                    data-testid="ausstattung-grid"
                >
                    {ITEMS.map((item, i) => {
                        const Icon = item.icon;
                        return (
                            <article
                                key={i}
                                className="bg-white p-8 lg:p-10 group transition-colors hover:bg-cream"
                                data-testid={`ausstattung-card-${i}`}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className="w-12 h-12 flex items-center justify-center bg-brand-50 text-brand-600 group-hover:bg-brand-500 group-hover:text-white transition-colors">
                                        <Icon size={22} strokeWidth={1.5} />
                                    </div>
                                    <span className="font-body text-[10px] uppercase tracking-[0.22em] text-stone-400">
                                        0{i + 1}
                                    </span>
                                </div>
                                <h3 className="font-heading text-xl lg:text-2xl text-ink leading-snug">
                                    {item.title}
                                </h3>
                                <p className="font-body text-sm text-stone-600 leading-relaxed mt-3">
                                    {item.text}
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {item.tags.map((tag, ti) => (
                                        <span
                                            key={ti}
                                            className="px-2.5 py-1 rounded-full text-[10px] font-body uppercase tracking-[0.16em] text-stone-700 bg-cream border border-stone-200"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>

                <p className="font-body text-xs text-stone-500 mt-8 max-w-3xl leading-relaxed">
                    {t({
                        de: "Genaue Ausstattungsdetails (Hersteller, Modelle, Optionen) werden in der individuellen Baubeschreibung dokumentiert. Smart-Home-Vollinstallation und einzelne Premium-Optionen können je nach Wohnung als Sonderwunsch beauftragt werden.",
                        en: "Exact equipment details (manufacturers, models, options) are documented in the individual building specification. Full smart-home installation and selected premium options can be commissioned as bespoke add-ons per apartment.",
                    })}
                </p>
            </div>
        </section>
    );
};
